import { Component, inject, signal } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ApiService } from '../../api.service';
import { InternalToolbar } from '../../components/internal-toolbar/internal-toolbar';
import { ReactiveIconTextButton } from '../../components/reactive-icon-text-button/reactive-icon-text-button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { TAGSongRequest } from '../../../models/tagsongrequest';
import { TagModule } from 'primeng/tag';
import {
  ActionConfirmationDialog,
  ActionConfirmationInfo,
} from '../../components/action-confirmation/action-confirmation-dialog';
import {
  newProgressDialogStep,
  ProgressDialogStep,
  ProgressDialogStepState,
  openProgressDialog,
} from '../../components/progressDialog/progressDialog';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MatIcon } from '../../components/mat-icon/mat-icon';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-internal-musicwishes',
  imports: [
    InternalToolbar,
    ReactiveIconTextButton,
    TranslatePipe,
    ToggleSwitchModule,
    FormsModule,
    TagModule,
    MessageModule,
    SkeletonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ButtonModule,
    MatIcon,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './internal-songrequests.html',
  styleUrl: './internal-songrequests.scss',
})
export class InternalSongRequests {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  //private router = inject(Router);
  //private route = inject(ActivatedRoute);
  public api = inject(ApiService);
  private sanitizer = inject(DomSanitizer);

  songRequestsEnabled = signal(undefined);
  songRequests = signal<TAGSongRequest[]>([]);
  loadingSongRequests = signal(true);

  savingRequestList = signal<string[]>([]);

  reloadTimer: any = undefined;

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.songRequestsEnabled.set(this.api.config().enableSongRequests);

      if (this.songRequestsEnabled()) this.toggleAutoReload(true);

      this.loadSongRequests();
    });
  }

  ngOnDestroy(): void {
    if (this.reloadTimer) clearInterval(this.reloadTimer);
  }

  toggleAutoReload(enabled: boolean) {
    if (enabled) {
      if (this.reloadTimer) clearInterval(this.reloadTimer);
      this.reloadTimer = setInterval(() => {
        this.loadSongRequests();
      }, 1000);
    } else {
      if (this.reloadTimer) clearInterval(this.reloadTimer);
      this.reloadTimer = undefined;
    }
  }

  loadSongRequests() {
    if (this.loadingSongRequests()) {
      this.songRequests.set(Array.from({ length: 3 }).map((_, i) => new TAGSongRequest()));
    }

    this.api
      .getSongRequests()
      .then((songRequests: TAGSongRequest[]) => {
        songRequests.sort((a, b) => {
          if (a.played && !b.played) {
            return 1;
          } else if (!a.played && b.played) {
            return -1;
          } else {
            return b.votes - a.votes;
          }
        });

        this.songRequests.set(songRequests);
        this.loadingSongRequests.set(false);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotLoadSongRequests')(),
          detail: reason.message,
        });
      });
  }

  toggleSongRequestPlayed(songRequest: TAGSongRequest) {
    this.savingRequestList.update((w) => [...w, songRequest._id]);
    songRequest.played = !songRequest.played;

    this.api
      .updateSongRequest(songRequest)
      .then((result: TAGSongRequest) => {
        this.songRequests.update((w) => {
          w.splice(w.indexOf(songRequest), 1, result);
          return [...w];
        });

        this.savingRequestList.update((w) => {
          w.splice(w.indexOf(songRequest._id), 1);
          return [...w];
        });
        this.loadSongRequests();
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotUpdateSongRequestsPlayed')(),
          detail: reason.message,
        });
      });
  }

  copySongRequest(songRequest: TAGSongRequest) {
    navigator.clipboard
      .writeText(songRequest.artist + ' ' + songRequest.title)
      .then(() => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.translate('internal.songrequests.copy.copied')(),
          detail: songRequest.artist + ' - ' + songRequest.title,
        });
      })
      .catch((err) => {
        this.messageService.add({
          severity: 'danger',
          summary: this.translate.translate('internal.songrequests.copy.failed')(),
          detail: songRequest.artist + ' - ' + songRequest.title,
        });
        console.error('Failed to copy text: ', err);
      });
  }

  getSpotifySearchUrl(artist: string, title: string): SafeUrl  {
    if (!artist && !title) return '';
    const query = encodeURIComponent(`${artist} ${title}`);
    return this.sanitizer.bypassSecurityTrustUrl(`spotify:search:${query}`);
  }

  deleteSongRequest(songRequest: TAGSongRequest) {
    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.songrequests.deleteSongRequest.title', songRequest)(),
        this.translate.translate('internal.songrequests.deleteSongRequest.message', songRequest)(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.songrequests.deleteSongRequest.progress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.deleteSongRequest(songRequest._id);
            this.songRequests.update((w) => {
              w.splice(w.indexOf(songRequest), 1);
              return [...w];
            });
            this.loadSongRequests();
            deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
          } catch (reason) {
            deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            deleteStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }

  deleteAllSongRequests() {
    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.songrequests.deleteAllSongRequests.title', {
          amount: this.songRequests().length,
        })(),
        this.translate.translate('internal.songrequests.deleteAllSongRequests.message', {
          amount: this.songRequests().length,
        })(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.songrequests.deleteAllSongRequests.progress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.deleteAllSongRequests();
            deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            this.loadingSongRequests.set(true);
            this.loadSongRequests();
            await pDialog.finishDialog();
          } catch (reason) {
            deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            deleteStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }
}
